import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { TableAction, TableColumn, RadioStation } from '@/types';
import { Edit, Eye, Plus, Search, Trash2 } from 'lucide-react';
import { useConfirmDialog, useCountriesList, useRadioCategoriesForDropdown, useTranslation } from '@/hooks';
import toast from 'react-hot-toast';
import {
  useRadioStations,
  useDeleteRadioStation,
  useToggleRadioStationStatus,
} from '@/hooks';
import DataTableWithQuery from '@/components/ui/DataTableWithQuery';
import Toggle from '@/components/ui/Toggle';
import Button from '@/components/ui/Button';
import StreamPlayerButton from '@/components/ui/StreamPlayerButton';
import AutocompleteSelect from '@/components/ui/AutocompleteSelect';
import { useMemo, useState } from 'react';
import { useBranding } from '@/context/BrandingContext';

const RadioStations = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { branding } = useBranding();
  const showCountryField = branding.modules?.radioCountriesEnabled === true;
  const { confirm, dialog } = useConfirmDialog();
  const [categorySearch, setCategorySearch] = useState('');
  const selectedCategoryId = searchParams.get('categories') ?? '';
  const { data: categories } = useRadioCategoriesForDropdown();
  const [countrySearch, setCountrySearch] = useState('');
  const selectedCountryId = searchParams.get('country.id') ?? '';
  const { data: countries } = useCountriesList();

  // Mutations
  const deleteRadioStationMutation = useDeleteRadioStation();
  const toggleStatusMutation = useToggleRadioStationStatus();

  const getRawStreamUrl = (station: RadioStation) => {
    const streamUrl = typeof station.streamUrl === 'string' ? station.streamUrl : '';
    return streamUrl.trim();
  };

  const categoryOptions = useMemo(
    () => (categories ?? []).map((category) => ({
      value: category.id,
      label: category.description || category.englishDescription,
    })),
    [categories]
  );

  const filteredCategoryOptions = useMemo(() => {
    const query = categorySearch.trim().toLowerCase();
    if (!query) return categoryOptions;
    return categoryOptions.filter((option) => option.label.toLowerCase().includes(query));
  }, [categoryOptions, categorySearch]);

  const countryOptions = useMemo(
    () =>
      (countries ?? []).map((country) => ({
        value: country.id,
        label: country.nameEnglish || country.name,
      })),
    [countries]
  );

  const filteredCountryOptions = useMemo(() => {
    const query = countrySearch.trim().toLowerCase();
    if (!query) return countryOptions;
    return countryOptions.filter((option) => option.label.toLowerCase().includes(query));
  }, [countryOptions, countrySearch]);

  const initialFilters = useMemo(
    () => ({
      categories: selectedCategoryId || undefined,
      ...(showCountryField ? { 'country.id': selectedCountryId || undefined } : {}),
    }),
    [selectedCategoryId, selectedCountryId, showCountryField]
  );

  const updateFilterParam = (key: 'categories' | 'country.id', value: string) => {
    const nextParams = new URLSearchParams(searchParams);

    if (value) {
      nextParams.set(key, value);
    } else {
      nextParams.delete(key);
    }

    setSearchParams(nextParams);
  };

  const radioStationColumns: TableColumn<RadioStation>[] = [
    {
      key: 'stationLogo',
      header: '',
      accessor: (station) => (
        <div className="flex items-center">
          {station.stationLogo ? (
            <img
              src={station.stationLogo}
              alt={station.stationName}
              className="h-8 w-auto object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          ) : (
            <div className="h-8 w-8 bg-gray-200 rounded flex items-center justify-center">
              <span className="text-xs text-gray-500">N/A</span>
            </div>
          )}
        </div>
      ),
      className: 'w-16'
    },
    {
      sortable: true,
      key: 'code',
      header: t.radioStations.fields.code,
      className: 'w-32',
      accessor: (station) => (
        <div className="flex items-center">
          <span
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {station.code}
          </span>
        </div>
      )
    },
    {
      sortable: true,
      key: 'stationName',
      header: t.radioStations.fields.stationName,
      accessor: (station) => (
        <div className="font-medium text-gray-900">
          <Link
            className="link"
            to={`/content/radio-stations/${station.id}/edit`}>
            {station.stationName}
          </Link>
        </div>
      ),
      className: 'min-w-48'
    },
    ...(showCountryField
      ? [{
          sortable: true,
          key: 'country.id',
          header: t.tvChannels.fields.country,
          accessor: (channel: RadioStation) => (
            <div className="text-gray-700">
              {channel.country?.flag && (
                /^https?:\/\//.test(channel.country.flag) ? (
                  <img
                    src={channel.country.flag}
                    alt={`${channel.country.name} flag`}
                    className="inline-block h-5 w-8 object-cover mr-2"
                  />
                ) : (
                  <span className="inline-block text-lg mr-2" aria-hidden="true">
                    {channel.country.flag}
                  </span>
                )
              )}
            </div>
          )
        } satisfies TableColumn<RadioStation>]
      : []),
    {
      sortable: true,
      key: 'active',
      header: t.radioStations.fields.active,
      accessor: (station) => (
        <Toggle
          checked={station.active}
          onChange={(checked) => handleToggleStatus(station.id, checked)}
          disabled={toggleStatusMutation.isPending}
          size="sm"
        />
      )
    },
    {
      key: 'play',
      header: '',
      className: 'w-5',
      align: 'center',
      accessor: (station) => (
        <StreamPlayerButton
          title={station.stationName}
          streamUrl={getRawStreamUrl(station)}
          playLabel={t.radioStations.actions.play}
          noPlayableStreamMessage={t.radioStations.messages.noPlayableStream}
          rtspInlineNotSupportedMessage={t.radioStations.messages.rtspInlineNotSupported}
        />
      )
    }
  ];

  const radioStationActions: TableAction<RadioStation>[] = [
    {
      label: t.radioStations.actions.edit,
      icon: Edit,
      onClick: (station) => navigate(`/content/radio-stations/${station.id}/edit`),
      className: 'text-gray-700 hover:text-indigo-600'
    },
    {
      label: t.radioStations.actions.activate,
      icon: Eye,
      onClick: (station) => handleToggleStatus(station.id, !station.active),
      className: 'text-gray-700 hover:text-yellow-600',
      show: (station) => !station.active
    },
    {
      label: t.radioStations.actions.deactivate,
      icon: Eye,
      onClick: (station) => handleToggleStatus(station.id, !station.active),
      className: 'text-gray-700 hover:text-yellow-600',
      show: (station) => station.active
    },
    {
      label: t.radioStations.actions.delete,
      icon: Trash2,
      onClick: (station) => handleDeleteRadioStation(station.id),
      className: 'text-red-600 hover:text-red-800'
    }
  ];

  const handleCreateRadioStation = () => {
    navigate('/content/radio-stations/create');
  };

  const handleDeleteRadioStation = async (id: string) => {
    try {
      const confirmed = await confirm({
        title: t.common.confirmationTitle,
        message: t.radioStations.messages.confirmDelete,
        confirmLabel: t.buttons.delete,
        cancelLabel: t.buttons.cancel,
        confirmVariant: 'danger',
        onConfirm: async () => {
          await deleteRadioStationMutation.mutateAsync(id);
        },
      });

      if (!confirmed) {
        return;
      }
      toast.success(t.radioStations.messages.deleted);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const handleToggleStatus = async (id: string, active: boolean) => {
    try {
      await toggleStatusMutation.mutateAsync({ id, active });
      toast.success(t.radioStations.messages.statusUpdated);
    } catch (error) {
      console.error('Toggle status error:', error);
      toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <DataTableWithQuery
          showTotalCount
          tableStyle="compact"
          columns={radioStationColumns}
          actions={radioStationActions}
          useQueryHook={useRadioStations}
          headerRenderer={({ searchQuery, onSearchChange, searchPlaceholder, totalCount }) => (
            <div className="border-b border-gray-200 bg-white">
              <div className="px-4 py-2 md:hidden">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-gray-900">{t.radioStations.listTitle}</h3>
                    <span className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                      {totalCount}
                    </span>
                  </div>
                  <Button onClick={handleCreateRadioStation} size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    Nueva radio
                  </Button>
                </div>
                <div className="relative mt-3">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="w-full rounded-md border border-gray-300 py-1.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div className="mt-3">
                  <AutocompleteSelect
                    value={selectedCategoryId}
                    onChange={(value) => {
                      updateFilterParam('categories', value);
                      setCategorySearch('');
                    }}
                    onSearch={setCategorySearch}
                    options={filteredCategoryOptions}
                    placeholder={t.radioStations.placeholders.allCategories}
                    size="sm"
                    className="text-base"
                    allowClear
                  />
                </div>
                {showCountryField && (
                  <div className="mt-3">
                    <AutocompleteSelect
                      value={selectedCountryId}
                      onChange={(value) => {
                        updateFilterParam('country.id', value);
                        setCountrySearch('');
                      }}
                      onSearch={setCountrySearch}
                      options={filteredCountryOptions}
                      placeholder={t.radioStations.placeholders.allCountries}
                      size="sm"
                      className="text-base"
                      allowClear
                    />
                  </div>
                )}
              </div>
              <div className="hidden flex-wrap items-center gap-3 px-4 py-2 md:flex">
                <h3 className="text-lg font-medium text-gray-900">{t.radioStations.listTitle}</h3>
                <span className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                  {totalCount}
                </span>
                <div className="relative flex-1 min-w-[220px]">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="w-full rounded-md border border-gray-300 py-1.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div className="min-w-[180px]">
                  <AutocompleteSelect
                    value={selectedCategoryId}
                    onChange={(value) => {
                      updateFilterParam('categories', value);
                      setCategorySearch('');
                    }}
                    onSearch={setCategorySearch}
                    options={filteredCategoryOptions}
                    placeholder={t.radioStations.placeholders.allCategories}
                    size="sm"
                    className="text-base"
                    allowClear
                  />
                </div>
                {showCountryField && (
                  <div className="min-w-[180px]">
                    <AutocompleteSelect
                      value={selectedCountryId}
                      onChange={(value) => {
                        updateFilterParam('country.id', value);
                        setCountrySearch('');
                      }}
                      onSearch={setCountrySearch}
                      options={filteredCountryOptions}
                      placeholder={t.radioStations.placeholders.allCountries}
                      size="sm"
                      className="text-base"
                      allowClear
                    />
                  </div>
                )}
                <div className="ml-auto">
                  <Button onClick={handleCreateRadioStation} size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    Nueva radio
                  </Button>
                </div>
              </div>
            </div>
          )}
          onCreate={handleCreateRadioStation}
          searchPlaceholder={t.radioStations.placeholders.search}
          title={t.radioStations.listTitle}
          itemsPerPage={10}
          initialFilters={initialFilters}
        />
        {dialog}
      </div>
    </div>
  );
};

export default RadioStations;
